import React, { useState, useEffect } from 'react';
import { apiRequest } from '@/utils/api-request';
import { Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, MapPin } from 'lucide-react';
import { showToast } from '@/components/ui/Toast';
import Button from '@/components/ui/Button';

interface CityData {
    cityId: number;
    cityName: string;
    is_popular: number;
    schools_count: number;
    country_id: number | null;
}

interface Pagination {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
}

const CitiesAdmin: React.FC = () => {
    const [cities, setCities] = useState<CityData[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [pagination, setPagination] = useState<Pagination>({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 25,
    });
    const [sortColumn, setSortColumn] = useState('schools_count');
    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC');
    const [countryFilter, setCountryFilter] = useState<number | null>(null); // null = All

    // Fetch cities
    const fetchCities = async (page: number = pagination.currentPage) => {
        try {
            setIsLoading(true);
            const response = await apiRequest('/api/cities', {
                method: 'POST',
                body: JSON.stringify({
                    page,
                    limit: pagination.itemsPerPage,
                    search_term: searchTerm,
                    sort_column: sortColumn,
                    sort_order: sortDirection,
                    country_id: countryFilter,
                }),
            });

            if (response.success) {
                setCities(response.data.cities || []);
                setPagination(response.data.pagination);
            } else {
                showToast('Failed to fetch cities', 'error');
            }
        } catch (error) {
            console.error('Error fetching cities:', error);
            showToast('Failed to fetch cities', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    // Load cities on mount and when filters change
    useEffect(() => {
        fetchCities(1);
    }, [searchTerm, sortColumn, sortDirection, countryFilter]);

    // Handle page change
    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            fetchCities(newPage);
        }
    };

    // Handle sort
    const handleSort = (column: string) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortColumn(column);
            setSortDirection('ASC');
        }
    };

    return (
        <div className="h-full flex flex-col bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-8 py-4">
                <div className="flex items-center gap-4">
                    {/* Left Side - Icon, Title with Badge */}
                    <div className="flex items-center gap-3 flex-1">
                        <div className="flex-shrink-0">
                            <div className="p-2 bg-brandColor rounded-lg text-white">
                                <MapPin className="h-5 w-5" />
                            </div>
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3">
                                <h1 className="text-lg font-semibold text-gray-900">Cities Management</h1>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-brandColor text-white">
                                    {pagination.totalItems.toLocaleString()}
                                </span>
                            </div>
                            <p className="text-xs text-gray-500">Manage cities and locations</p>
                        </div>
                    </div>

                    {/* Right Side - Country filter + Search */}
                    <div className="flex items-center gap-3 flex-1">
                        {/* Country tabs */}
                        <div className="flex rounded-md border border-gray-300 overflow-hidden text-sm">
                            {[{ label: 'All', value: null }, { label: '🇮🇳 India', value: 1 }, { label: '🇦🇪 UAE', value: 2 }].map(opt => (
                                <button
                                    key={String(opt.value)}
                                    onClick={() => setCountryFilter(opt.value)}
                                    className={`px-3 py-2 font-medium transition-colors ${countryFilter === opt.value ? 'bg-brandColor text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                            <input
                                type="text"
                                placeholder="Search cities..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto px-4 mt-4">
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full table-auto border-collapse">
                        <thead className="bg-brandColor text-white sticky top-0 z-10">
                            <tr>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors"
                                    onClick={() => handleSort('cityId')}
                                >
                                    ID {sortColumn === 'cityId' && (sortDirection === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors"
                                    onClick={() => handleSort('cityName')}
                                >
                                    City Name {sortColumn === 'cityName' && (sortDirection === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th
                                    className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors"
                                    onClick={() => handleSort('is_popular')}
                                >
                                    Is Popular {sortColumn === 'is_popular' && (sortDirection === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                                    Country
                                </th>
                                <th
                                    className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors"
                                    onClick={() => handleSort('schools_count')}
                                >
                                    Schools Count {sortColumn === 'schools_count' && (sortDirection === 'ASC' ? '↑' : '↓')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                                        Loading cities...
                                    </td>
                                </tr>
                            ) : cities.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                                        No cities found
                                    </td>
                                </tr>
                            ) : (
                                cities.map((city, index) => (
                                    <tr
                                        key={city.cityId}
                                        className={`hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                                    >
                                        <td className="px-4 py-2 text-sm text-gray-600">
                                            {city.cityId}
                                        </td>
                                        <td className="px-4 py-2 text-sm">
                                            <span className="text-gray-800 font-semibold">
                                                {city.cityName}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2 text-sm text-center">
                                            {city.is_popular === 1 ? (
                                                <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                                                    Yes
                                                </span>
                                            ) : (
                                                <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">
                                                    No
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-center">
                                            {city.country_id === 2 ? (
                                                <span className="px-2 py-1 text-xs rounded-full bg-emerald-100 text-emerald-800 font-medium">🇦🇪 UAE</span>
                                            ) : city.country_id === 1 ? (
                                                <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-800 font-medium">🇮🇳 India</span>
                                            ) : (
                                                <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-500">—</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-center">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-brandColor text-white">
                                                {city.schools_count.toLocaleString()}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
                <div className="text-sm text-gray-600">
                    Showing {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} to{' '}
                    {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of{' '}
                    {pagination.totalItems} results
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(1)}
                        disabled={pagination.currentPage === 1}
                    >
                        <ChevronsLeft size={16} />
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                        disabled={pagination.currentPage === 1}
                    >
                        <ChevronLeft size={16} />
                    </Button>
                    <span className="text-sm text-gray-600">
                        Page {pagination.currentPage} of {pagination.totalPages}
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                        disabled={pagination.currentPage === pagination.totalPages}
                    >
                        <ChevronRight size={16} />
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.totalPages)}
                        disabled={pagination.currentPage === pagination.totalPages}
                    >
                        <ChevronsRight size={16} />
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default CitiesAdmin;
